import { useEffect, useState } from 'react'

const FALLBACK = '#ffffff'
// Cache is ephemeral (per page load) — no persistence, so algorithm
// changes take effect immediately without stale entries.
const cache = new Map<string, string>()

interface RGB {
  r: number
  g: number
  b: number
}

function rgbToHex({ r, g, b }: RGB): string {
  const to = (n: number) => n.toString(16).padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`
}

function dist(a: RGB, b: RGB): number {
  const dr = a.r - b.r
  const dg = a.g - b.g
  const db = a.b - b.b
  return Math.sqrt(dr * dr + dg * dg + db * db)
}

/**
 * Sample the outer border of the image (where a product photo's backdrop
 * almost always lives) and return the most common color, lightly
 * quantized. Returns FALLBACK when the image can't be read (CORS) or no
 * clear dominant color emerges.
 */
// Sentinel returned when the browser cannot read image pixels (CORS).
// Distinct from '#ffffff' so the hook knows to fall through to the server.
const CORS_FAIL = '__CORS_FAIL__'

function extractBackground(img: HTMLImageElement): string {
  const canvas = document.createElement('canvas')
  const w = Math.min(img.naturalWidth, 120)
  const h = Math.min(img.naturalHeight, 120)
  if (w === 0 || h === 0) return FALLBACK
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return FALLBACK
  ctx.drawImage(img, 0, 0, w, h)

  let data: Uint8ClampedArray
  try {
    data = ctx.getImageData(0, 0, w, h).data
  } catch {
    return CORS_FAIL
  }

  const quantize = (v: number) => Math.round(v / 20) * 20

  // Sample the 1px ring inset by `fraction` of the smaller dimension and
  // return its dominant color + how much of the ring it covers.
  const sampleRing = (fraction: number): { color: RGB; coverage: number } | null => {
    const inset = Math.round(fraction * Math.min(w, h))
    const x0 = inset
    const y0 = inset
    const x1 = w - 1 - inset
    const y1 = h - 1 - inset
    if (x1 <= x0 || y1 <= y0) return null

    const buckets = new Map<string, { sum: RGB; count: number }>()
    let total = 0

    const push = (x: number, y: number) => {
      const i = (y * w + x) * 4
      const alpha = data[i + 3]
      if (alpha < 128) return
      const c: RGB = { r: data[i], g: data[i + 1], b: data[i + 2] }
      const key = `${quantize(c.r)},${quantize(c.g)},${quantize(c.b)}`
      const bucket = buckets.get(key)
      if (bucket) {
        bucket.sum.r += c.r
        bucket.sum.g += c.g
        bucket.sum.b += c.b
        bucket.count += 1
      } else {
        buckets.set(key, { sum: { ...c }, count: 1 })
      }
      total += 1
    }

    for (let x = x0; x <= x1; x++) push(x, y0)
    for (let x = x0; x <= x1; x++) push(x, y1)
    for (let y = y0 + 1; y < y1; y++) push(x0, y)
    for (let y = y0 + 1; y < y1; y++) push(x1, y)

    if (total === 0 || buckets.size === 0) return null
    const top = [...buckets.values()].sort((a, b) => b.count - a.count)[0]
    if (top.count < 2) return null
    return {
      color: {
        r: Math.round(top.sum.r / top.count),
        g: Math.round(top.sum.g / top.count),
        b: Math.round(top.sum.b / top.count),
      },
      coverage: top.count / total,
    }
  }

  const isNearWhite = (c: RGB): boolean => {
    const maxC = Math.max(c.r, c.g, c.b)
    const minC = Math.min(c.r, c.g, c.b)
    if ((maxC + minC) / 2 > 245 && maxC - minC < 10) return true
    return dist(c, { r: 255, g: 255, b: 255 }) < 6
  }

  const isTooDark = (c: RGB): boolean => {
    const maxC = Math.max(c.r, c.g, c.b)
    const minC = Math.min(c.r, c.g, c.b)
    return (maxC + minC) / 2 < 20
  }

  // Start from the outermost 1px edge — the true backdrop color.
  const edge = sampleRing(0)
  if (!edge) return FALLBACK

  if (isNearWhite(edge.color)) {
    // The edge is white, but some product photos have a thin white FRAME
    // baked into the file around a colored backdrop. Walk a few % inward:
    // if a non-white color takes over the whole ring, that is the backdrop.
    for (const f of [0.02, 0.03, 0.04, 0.05, 0.06, 0.08, 0.1]) {
      const ring = sampleRing(f)
      if (!ring) continue
      if (isTooDark(ring.color)) continue
      if (!isNearWhite(ring.color) && ring.coverage >= 0.65) {
        return rgbToHex(ring.color)
      }
    }
    return '#ffffff'
  }

  if (isTooDark(edge.color)) return FALLBACK
  return rgbToHex(edge.color)
}

/**
 * Detects the backdrop color of a product image so the container behind it
 * can be tinted to match (e.g. Zalando-style light grey instead of white).
 *
 * Goes through the image-background Edge Function in proxy mode: the server
 * fetches the bytes (no CORS limits), the browser decodes them natively —
 * this is what makes WebP uploads work, since the server-side decoder
 * cannot read WebP — and the blob URL keeps the canvas untainted.
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

async function detectViaProxy(imageUrl: string): Promise<string> {
  try {
    const base = import.meta.env.VITE_SUPABASE_URL as string
    if (!base) return FALLBACK
    const res = await fetch(`${base}/functions/v1/image-background`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: imageUrl, proxy: true }),
    })
    if (!res.ok) return FALLBACK
    const blob = await res.blob()
    if (!blob.type.startsWith('image/')) return FALLBACK
    const objectUrl = URL.createObjectURL(blob)
    try {
      const img = await loadImage(objectUrl)
      const detected = extractBackground(img)
      return detected === CORS_FAIL ? FALLBACK : detected
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  } catch {
    return FALLBACK
  }
}

export function useImageBackground(imageUrl: string | null | undefined): string {
  const [color, setColor] = useState<string>(
    imageUrl ? cache.get(imageUrl) ?? FALLBACK : FALLBACK,
  )

  useEffect(() => {
    if (!imageUrl) {
      setColor(FALLBACK)
      return
    }
    const cached = cache.get(imageUrl)
    if (cached) {
      setColor(cached)
      return
    }

    let cancelled = false

    const apply = (detected: string) => {
      if (cancelled) return
      cache.set(imageUrl, detected)
      setColor(detected)
    }

    // Images hosted on Supabase Storage often lack permissive CORS headers,
    // so the browser canvas read silently taints/fails.  Skip the client
    // attempt entirely for those URLs and go straight to the proxy.
    const isSupabaseStorage = /\.supabase\.(co|dev|net)\//i.test(imageUrl)

    if (isSupabaseStorage) {
      void detectViaProxy(imageUrl).then(apply)
      return
    }

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (cancelled) return
      const detected = extractBackground(img)
      if (detected !== CORS_FAIL) {
        apply(detected === FALLBACK ? '#ffffff' : detected)
        return
      }
      void detectViaProxy(imageUrl).then(apply)
    }
    img.onerror = () => {
      void detectViaProxy(imageUrl).then(apply)
    }
    img.src = imageUrl

    return () => {
      cancelled = true
    }
  }, [imageUrl])

  return color
}
